import { Form, Formik } from "formik";
import { useState } from "react";

import {
  caseDetailsSchema,
  caseDetailsWarningSchema,
} from "shared/utils/caseSchema";
import {
  formatTimestampForDateTime,
  prepareDatetimeStringForAPI,
} from "shared/utils/dates";
import { getBillingTypeFromAvailableTypes } from "shared/utils/rulesEngineUtils";
import { getValidationWarnings } from "shared/utils/validationUtils";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { updateCase } from "app/reducers/admin/actions/caseActions";
import { CaseState } from "app/reducers/admin/reducers/caseReducer";
import { PatientState } from "app/reducers/admin/reducers/patientReducer";
import { useSelectedLab } from "hooks/useSelectedLabStation";
import CaseDetailsForm from "ui/CaseForms/CaseDetailsForm";

const DetailsForm = ({
  caseDetail,
  patient,
}: {
  caseDetail?: CaseState;
  patient: PatientState;
}) => {
  const dispatch = useAppDispatch();

  const lab = useSelectedLab();

  const clinics = useAppSelector((state) => state.clinics);

  const initialValues = {
    external_accession: caseDetail?.external_accession || "",

    bill_tc: caseDetail?.bill_tc !== false,
    bill_pc: caseDetail?.bill_pc !== false,
    self_pay: caseDetail?.self_pay,

    active_assignment: caseDetail?.active_assignment,
    ordering_doctor: caseDetail?.ordering_doctor,

    collected_date:
      caseDetail?.collected_date &&
      formatTimestampForDateTime(
        caseDetail.collected_date,
        caseDetail?.collected_date_timezone ||
          patient?.clinic?.location?.timezone
      ),
    received_date:
      caseDetail?.received_date &&
      formatTimestampForDateTime(
        caseDetail.received_date,
        caseDetail?.received_date_timezone || lab?.location?.timezone
      ),

    clinic: patient?.clinic,

    billing_type: getBillingTypeFromAvailableTypes(
      caseDetail?.billing_type || undefined,
      clinics[patient?.clinic?.id || 0]?.billing_types
    ),
  };
  const [warnings, setWarnings] = useState({});

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => {
        // Don't include the full objects where unnecessary
        const { ordering_doctor, active_assignment, clinic, ...rest } = values;
        dispatch(
          updateCase({
            id: caseDetail?.id || 0,
            ...rest,
            ...(rest.collected_date && {
              collected_date: prepareDatetimeStringForAPI(
                rest.collected_date,
                caseDetail?.collected_date_timezone ||
                  clinic?.location?.timezone
              ),
            }),
            ...(rest.received_date && {
              received_date: prepareDatetimeStringForAPI(
                rest.received_date,
                caseDetail?.received_date_timezone || lab?.location?.timezone
              ),
            }),
            //@ts-ignore
            active_assignment_id: active_assignment?.id,
            ordering_doctor_id: ordering_doctor?.id,

            billing_type: rest.billing_type,
          })
        );
      }}
      validate={(values: any) => {
        caseDetailsWarningSchema
          .validate(values, {
            abortEarly: false,
          })
          .then(() => {
            setWarnings({});
          })
          .catch((err) => {
            setWarnings(getValidationWarnings(err));
          });
      }}
      validateOnBlur={false}
      validationSchema={caseDetailsSchema}
      validateOnMount={true}
    >
      {({ values, setValues, dirty, errors, touched }) => (
        <Form autoComplete="off">
          <CaseDetailsForm
            values={values}
            errors={errors}
            touched={touched}
            warnings={warnings}
            setValues={setValues}
            displaySubmit
            editingAccession={{
              ...caseDetail,
              clinic: patient.clinic,
            }}
            dirty={dirty}
          />
        </Form>
      )}
    </Formik>
  );
};

export default DetailsForm;
